import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../components/Button";
import { ICostCenter } from "../../../@types/costCenter.type";
import { getAllCostCenters } from "../../../services/api/costCenter/costCenterService";
import { IApiResponse } from "../../../@types/api-response.type";
import vectorIcon from "../../../assets/_VectorIcon_.svg";
import addCircleImage from "./../../../assets/_AddCircleIcon_.svg";
import usePermission from "../../../hooks/usePermission";
import { FeatureStatic } from "../../../@types/statics/feature.static";
import styles from "./styles.module.scss";

import MuiButton from "@mui/material/Button";
import { ColDef, FilterChangedEvent, GridReadyEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { themeGrid } from "../../../themeGrid";
import { localeText } from "../../../localeTextFromGrid";
import { getFromCache, saveToCache } from "../../../utils/setAndGetFromStorage";
import { useSyncActive } from "../../../utils/useSyncActive";

export const CostCenter: React.FC = () => {
  const [coastCenterItems, setCoastCenterItems] = useState<ICostCenter[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const gridRef = useRef<AgGridReact<any>>(null);

  const isSyncActive = useSyncActive();

  const navigate = useNavigate();
  const permissions = usePermission(FeatureStatic.costCenter.id);

  const columns: ColDef[] = [
    {
      field: "code",
      headerName: "Código",
      minWidth: 300,
      flex: 1,
    },
    {
      field: "name",
      headerName: "Nome",
      minWidth: 300,
      flex: 1,
    },
    {
      field: "alias",
      headerName: "Apelido",
      minWidth: 300,
      flex: 1,
    },
  ];

  const [cachedFilters, setCachedFilters] = useState(
    getFromCache("agGridFiltersCostCenter")
  );

  const [cachedSort, setCachedSort] = useState(
    getFromCache("agGridSortCostCenter")
  );

  const onFilterChanged = (params: FilterChangedEvent) => {
    const filters = params.api.getFilterModel(); // Obtém o modelo de filtro
    saveToCache("agGridFiltersCostCenter", filters); // Salva no cache
  };

  const onSortChanged = () => {
    const colState = gridRef.current!.api.getColumnState();
    const sortState = colState
      .filter(function (s) {
        return s.sort != null;
      })
      .map(function (s) {
        return { colId: s.colId, sort: s.sort, sortIndex: s.sortIndex };
      });

    saveToCache("agGridSortCostCenter", sortState);
  };

  const onGridReady = (params: GridReadyEvent) => {
    const gridApi = params.api;

    // Restaurar filtros
    if (cachedFilters) {
      gridApi.setFilterModel(cachedFilters);
    }

    // Restaurar ordenação
    if (cachedSort) {
      gridApi.applyColumnState({
        state: cachedSort,
        defaultState: { sort: null },
      });
    }
  };

  useEffect(() => {
    if (!permissions.canView()) {
      navigate("/unauthorized");
      return;
    }

    async function fetchAllCostCenters() {
      try {
        setLoading(true);

        const response = (await getAllCostCenters()) as IApiResponse<
          ICostCenter[]
        >;
        setCoastCenterItems(response.result);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    }

    fetchAllCostCenters();
  }, []);

  function navigateToUpdatePage(id: string) {
    navigate(`update/${id}`);
  }

  return (
    <div className={styles.costCenter}>
      <div className={styles.back}>
        <MuiButton
          className={styles.buttonMui}
          onClick={() => navigate("/configurations")}
        >
          {vectorIcon && <img src={vectorIcon} />}
          Voltar a tela anterior
        </MuiButton>
      </div>
      <div className={styles.titleAndBtnCreate}>
        <h1>Centro de Custo</h1>

        <div className={styles.btnsFilterAndCreate}>
          {!isSyncActive && (
            <Button
              iconUrl={addCircleImage}
              onClick={() => {
                navigate("/configurations/costCenter/create");
              }}
              content="Novo Centro de Custo"
              className={styles.button}
            />
          )}
        </div>
      </div>

      <div className={`${styles.dataGridContainer}`}>
        <AgGridReact
          ref={gridRef}
          rowData={coastCenterItems}
          columnDefs={columns}
          theme={themeGrid}
          localeText={localeText}
          loading={loading}
          overlayNoRowsTemplate="<span style='color: gray;'>Sem registros encontrados</span>"
          defaultColDef={{
            sortable: true,
            editable: false,
            filter: true,
            floatingFilter: true,
          }}
          suppressCellFocus={true}
          rowStyle={{ cursor: isSyncActive ? "default" : "pointer" }}
          onRowClicked={(row) => {
            if (!isSyncActive) navigateToUpdatePage(row.data.id);
          }}
          onFilterChanged={onFilterChanged}
          onSortChanged={onSortChanged}
          animateRows={true}
          onGridReady={onGridReady}
        />
      </div>
    </div>
  );
};
